import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  ComponentRef,
  DestroyRef,
  Injectable,
  ViewContainerRef,
  inject,
} from '@angular/core';
import {
  FormConfiguration,
  FormMode,
  FormPanel,
  FormProperty,
} from '../models/form.models';
import {
  NomAlertDeviationDirection,
  NomAlertTriggerType,
  NomExternalShareOption,
  NomLanguages,
  NomOutputFormat,
} from '../constants';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from './nomenclature.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface FormOption {
  name: string;
  value: any; // can be boolean, string, number, etc.
  image?: string; // Optional, for images/icons
  icon?: IconProp;
}

export enum SensikaForms {
  USER_EDITOR = 'user-editor',
  COMPANY_EDITOR = 'company-editor',
}

interface SummaryFilter {
  value: string;
  name: string;
}

type TransformationFunction = (values: any) => any;

// Validator function to check if the array has at least one item
export function minArrayLength(min: number): ValidatorFn {
  return (array: FormArray): { [key: string]: any } | null => {
    const length = array.controls.filter(
      (c) => c.value !== null && c.value !== '',
    ).length;
    return length >= min
      ? null
      : { minArrayLength: { requiredLength: min, actualLength: length } };
  };
}

@Injectable({
  providedIn: 'root',
})
/**
 * Service for creating and manipulating forms.
 */
export class FormsService {
  protected destroyRef = inject(DestroyRef);
  protected logger = inject(LoggerService);
  protected nomenclatureService = inject(NomenclatureService);
  constructor(
    public formBuilder: FormBuilder,
    public translateService: TranslateService,
  ) {}

  private readonly DEFAULT_TOGGLE_VALUE = false;
  private readonly DEFAULT_CONTROL_VALUE = null;

  /**
   * Creates a FormGroup from a list of FormProperties.
   * @param formProperties The FormProperties to create the FormGroup from.
   * @returns The created FormGroup.
   */
  createFormGroup(
    formProperties: FormProperty[],
    changeNotifier: Subject<void> = null,
  ): FormGroup {
    const formGroup: FormGroup = this.formBuilder.group({});

    formProperties.forEach((prop) => {
      this.createControl(prop, formGroup);
    });

    this.setupFormDisplayControl(formGroup, formProperties, changeNotifier);

    return formGroup;
  }

  private setupFormDisplayControl(
    formGroup: FormGroup,
    formProperties: FormProperty[],
    changeNotifier: Subject<void> = null,
  ) {
    const visibilityChanger = () => {
      formProperties.forEach((prop) => {
        // Synchronous case
        if (prop.shouldDisplay) {
          const shouldDisplayValue = prop.shouldDisplay(formGroup);
          prop.shouldDisplaySubject.next(shouldDisplayValue);
          if (changeNotifier) {
            changeNotifier.next();
          }
        }
        // Asynchronous case
        if (prop.shouldDisplayAsync) {
          prop.shouldDisplayAsync(formGroup).subscribe((shouldDisplayValue) => {
            prop.shouldDisplaySubject.next(shouldDisplayValue);
            if (changeNotifier) {
              changeNotifier.next();
            }
          });
        }
      });
    };

    formProperties.forEach((prop) => {
      if (prop.shouldDisplay || prop.shouldDisplayAsync) {
        prop.shouldDisplaySubject = new BehaviorSubject<boolean>(true);
      }

      if (prop.formChanger) {
        let control = formGroup.get([prop.propertyName]);
        if (control) {
          control.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              visibilityChanger();
            });
        }
      }
    });

    // Initial evaluation of visibility
    this.evaluateVisibility(formGroup, formProperties);
  }

  private evaluateVisibility(
    formGroup: FormGroup,
    formProperties: FormProperty[],
  ) {
    formProperties.forEach((prop) => {
      if (prop.shouldDisplay) {
        const shouldDisplayValue = prop.shouldDisplay(formGroup);
        prop.shouldDisplaySubject.next(shouldDisplayValue);
      } else if (prop.shouldDisplaySubject) {
        prop.shouldDisplaySubject.next(true);
      }
    });
  }

  public createControl(prop: FormProperty, group: FormGroup): void {
    const validators = this.normalizeValidators(prop.validators);
    const defaultValue = this.getDefaultValue(prop);
    const isDisabled = prop.additionalConfig?.['disabled'] || false;

    if (prop.propertyNameLanguages) {
      this.createLanguageControls(prop, group, validators, isDisabled);
    } else if (prop.propertyNames) {
      this.createMultipleControls(
        prop.propertyNames,
        group,
        validators,
        isDisabled,
      );
    } else if (
      prop.controlType === 'checkbox-array' ||
      prop.controlType === 'structured-checkbox-array'
    ) {
      const controls = (defaultValue || []).map((value) =>
        this.formBuilder.control(value),
      );
      group.addControl(
        prop.propertyName,
        this.formBuilder.array(controls, validators),
      );
    } else if (prop.controlType === 'colorpicker-array') {
      this.buildFlatColorArrayControls(prop, group, validators, isDisabled);
    } else if (prop.controlType === 'component') {
    } else {
      group.addControl(
        prop.propertyName,
        this.formBuilder.control(
          { value: defaultValue, disabled: isDisabled },
          validators,
        ),
      );
    }
  }

  // Dynamically create a component and pass inputs
  createDynamicComponent(container: ViewContainerRef, prop: any): void {
    container.clear();

    // Create the component
    const componentRef: ComponentRef<any> = container.createComponent(
      prop.additionalConfig['component'],
    );

    prop.componentInstance = componentRef;

    if (prop.additionalConfig?.inputs) {
      const inputs = prop.additionalConfig.inputs;
      for (let key in inputs) {
        if (inputs.hasOwnProperty(key)) {
          componentRef.instance[key] = inputs[key];
        }
      }
    }

    // Detect changes after inputs are set
    componentRef.changeDetectorRef.detectChanges();
  }

  private normalizeValidators(
    validators: ValidatorFn | ValidatorFn[],
  ): ValidatorFn[] {
    return Array.isArray(validators)
      ? validators
      : validators
        ? [validators]
        : [];
  }

  private getDefaultValue(prop: FormProperty): any {
    if (prop.defaultValue !== undefined) {
      return prop.defaultValue;
    }

    const controlDefaults: Record<string, any> = {
      toggle: this.DEFAULT_TOGGLE_VALUE,
      'checkbox-array': [],
      'structured-checkbox-array': [],
    };

    return controlDefaults[prop.controlType] ?? this.DEFAULT_CONTROL_VALUE;
  }

  private createLanguageControls(
    prop: FormProperty,
    group: FormGroup,
    validators: ValidatorFn[],
    isDisabled: boolean = false,
  ): void {
    Object.keys(prop.propertyNameLanguages).forEach((lang, index) => {
      const langPropName = prop.propertyNameLanguages[lang];
      const controlValidators = index === 0 ? validators : [];
      group.addControl(
        langPropName,
        this.formBuilder.control(
          { value: '', disabled: isDisabled },
          controlValidators,
        ),
      );
    });
  }

  private createMultipleControls(
    propertyNames: string[],
    group: FormGroup,
    validators: ValidatorFn[],
    isDisabled: boolean = false,
  ): void {
    propertyNames.forEach((name) => {
      group.addControl(
        name,
        this.formBuilder.control(
          { value: '', disabled: isDisabled },
          validators,
        ),
      );
    });
  }

  private buildPanelControls(
    panel: FormPanel,
    formGroup: FormGroup,
    changeNotifier: Subject<void> = null,
  ): void {
    panel.items?.forEach((item) => {
      this.createControl(item, formGroup);
    });

    this.setupFormDisplayControl(formGroup, panel.items || [], changeNotifier);

    // Handle child panels recursively
    panel.panels?.forEach((childPanel) => {
      this.buildPanelControls(childPanel, formGroup, changeNotifier);
    });
  }

  createFormGroupByFormConfig(
    formConfiguration: FormConfiguration,
    changeNotifier: Subject<void> = null,
  ): FormGroup {
    const group = this.formBuilder.group({});

    formConfiguration.tabs.forEach((tab) => {
      tab.panels.forEach((panel) => {
        this.buildPanelControls(panel, group, changeNotifier);
      });
    });

    return group;
  }

  /**
   * Flattens an object by converting nested properties into dot-separated keys.
   * @param obj - The object to flatten.
   * @param parentKey - The parent key used for nested properties.
   * @param result - The result object to store the flattened properties.
   * @returns The flattened object.
   */
  flattenObject(
    obj: any,
    parentKey = '',
    result = {},
    explicitFields?: string[],
    seen: WeakSet<object> = new WeakSet(),
  ): any {
    if (obj && typeof obj === 'object') {
      if (seen.has(obj)) {
        return result;
      }
      seen.add(obj);
    }
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        let propName = parentKey ? parentKey + '.' + key : key;

        if (
          typeof obj[key] === 'object' &&
          obj[key] !== null &&
          (!Array.isArray(obj[key]) || explicitFields?.includes(propName))
        ) {
          this.flattenObject(obj[key], propName, result, explicitFields, seen);
        } else {
          result[propName] = obj[key];
        }
      }
    }
    return result;
  }

  /**
   * Converts a time string to an object with hour and minute properties.
   * @param timeString - The time string to convert (in the format "HH:MM").
   * @returns An object with hour and minute properties.
   */
  convertTimeStringToObject(timeString: string): {
    hour: number;
    minute: number;
  } {
    const [hourStr, minuteStr] = timeString.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    return { hour, minute };
  }

  /**
   * Converts an object representing time to a string in the format "HH:mm".
   * @param timeObj - The object containing the hour and minute values.
   * @returns A string representation of the time in the format "HH:mm".
   */
  convertObjectToTimeString(timeObj: { hour: number; minute: number }): string {
    return `${timeObj.hour.toString().padStart(2, '0')}:${timeObj.minute
      .toString()
      .padStart(2, '0')}`;
  }

  /**
   * Populates the values of a FormGroup with the provided values object.
   *
   * @param formGroup - The FormGroup to populate.
   * @param values - The values object containing the data to populate the FormGroup with.
   */
  populateFormValues(
    formGroup: FormGroup,
    values: any,
    transformFn?: TransformationFunction,
    flattenValues: boolean = true,
    flattenExplicitFields?: string[],
  ) {
    let flattenedValues = values;
    if (flattenValues) {
      flattenedValues = this.flattenObject(
        values,
        '',
        {},
        flattenExplicitFields,
      );
    }
    if (transformFn) {
      flattenedValues = transformFn(flattenedValues);
    }

    formGroup.patchValue(flattenedValues);

    for (const controlName in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(controlName)) {
        const control = formGroup.get([controlName]);

        if (control instanceof FormArray) {
          // Assuming 'inputValues' is an object where keys match the control names
          // and values are arrays to populate the FormArrays.
          const valuesToPopulate = flattenedValues[controlName];
          if (valuesToPopulate && Array.isArray(valuesToPopulate)) {
            this.populateFormArray(controlName, valuesToPopulate, formGroup);
          }
        }
      }
    }
  }

  /**
   * Populates a FormArray with data.
   *
   * @param formArrayName - The name of the FormArray.
   * @param data - An array of data to populate the FormArray with.
   * @param formGroup - The parent FormGroup containing the FormArray.
   */
  private populateFormArray(
    formArrayName: string,
    data: any[],
    formGroup: FormGroup,
  ) {
    const array = formGroup.get([formArrayName]) as FormArray;

    if (!array) {
      this.logger.warn(
        `FormArray with name '${formArrayName}' not found in FormGroup.`,
      );
      return;
    }

    array.clear(); // Clear existing array

    data.forEach((item) => {
      array.push(this.formBuilder.control(item));
    });
  }

  /** Creates/maintains root-level controls like `<base>.<index>` for a color array */
  private buildFlatColorArrayControls(
    prop: FormProperty,
    formGroup: FormGroup,
    validators: ValidatorFn | ValidatorFn[] | null,
    isDisabled: boolean,
  ): void {
    const base = prop.propertyName; // e.g. "settings.slice_color_id"
    const prefix = `${base}.`;
    const cfg = (prop as any).additionalConfig || {};
    const defaultColor: string = cfg.defaultColor ?? null; // e.g. "#000000"
    this.logger.debug(
      `Building color array controls for '${base}' with prefix '${prefix}' with defaultColor '${defaultColor}'`,
    );
    const lengthFromKey: string | undefined = cfg.lengthFrom; // e.g. "settings.slice_count"

    // Initial length:
    // 1) driver control (if any)
    // 2) defaultValue array length (if provided)
    // 3) existing matching controls (if any)
    const driverCtrl = lengthFromKey
      ? formGroup.get([lengthFromKey])
      : undefined;
    const initialLenFromDriver = this.asNumber(driverCtrl?.value);
    const initialLenFromDefault = Array.isArray(prop['defaultValue'])
      ? prop['defaultValue'].length
      : 10;
    const initialLenFromExisting = Object.keys(
      (formGroup as any).controls || {},
    ).filter((k) => k.startsWith(prefix)).length;

    const initialLen = Math.max(
      10,
      initialLenFromDriver ??
        initialLenFromDefault ??
        initialLenFromExisting ??
        0,
    );

    // Create the right number of controls and hydrate defaults
    this.ensureFlatIndexedControls({
      formGroup,
      base,
      length: initialLen,
      validators,
      isDisabled,
      defaultColor,
      defaultArray: Array.isArray(prop['defaultValue'])
        ? (prop['defaultValue'] as any[])
        : undefined,
    });

    // Keep in sync with driver (if configured)
    if (driverCtrl) {
      driverCtrl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((val) => {
          const len = this.asNumber(val) ?? 0;
          this.ensureFlatIndexedControls({
            formGroup,
            base,
            length: len,
            validators,
            isDisabled,
            defaultColor,
          });
        });
    }
  }

  /** Add missing `<base>.<i>` controls and remove extras, preserving existing values */
  private ensureFlatIndexedControls(opts: {
    formGroup: FormGroup;
    base: string;
    length: number;
    validators: ValidatorFn | ValidatorFn[] | null;
    isDisabled: boolean;
    defaultColor: any;
    defaultArray?: any[];
  }): void {
    const {
      formGroup: group,
      base,
      length,
      validators,
      isDisabled,
      defaultColor,
      defaultArray,
    } = opts;
    const prefix = `${base}.`;

    const currentValues = Array.isArray(group.get([prefix])?.value)
      ? group.get([prefix])?.value
      : null;

    // Add missing
    for (let i = 0; i < length; i++) {
      const key = `${prefix}${i}`;

      if (!this.hasFlat(group, key)) {
        const initial =
          currentValues && typeof currentValues[i] !== 'undefined'
            ? currentValues[i]
            : defaultArray && typeof defaultArray[i] !== 'undefined'
              ? defaultArray[i]
              : defaultColor;

        group.addControl(
          key,
          new FormControl({ value: initial, disabled: isDisabled }, validators),
        );
      }
    }

    // Remove extras
    Object.keys((group as any).controls || {})
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => {
        const idx = Number(k.slice(prefix.length));
        if (!Number.isFinite(idx) || idx < 0 || idx >= length) {
          group.removeControl(k);
        }
      });
  }

  private hasFlat(formGroup: FormGroup, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(
      (formGroup as any).controls || {},
      key,
    );
  }

  private asNumber(v: any): number | undefined {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }

  transformForSelect<T>(
    items: T[],
    valueExtractor: (item: T) => any,
    nameExtractor: (item: T) => string,
    // Placeholders are real placeholders, never fake options: "nothing selected" is
    // rendered by the dynamic form's additionalConfig.placeholder, so no default row
    // is injected. Callers pass null explicitly (see src/sui/CRAFTING-GUIDE.md).
    defaultOption: FormOption | null = null,
  ): Array<{ value: any; name: string }> {
    const transformed = items.map((item) => ({
      value: valueExtractor(item),
      name: nameExtractor(item),
    }));

    // A caller-supplied default option (a real domain value, never a placeholder) goes first
    return defaultOption ? [defaultOption, ...transformed] : transformed;
  }

  markAllAsTouchedAndFocusFirstInvalid(formGroup: FormGroup) {
    let firstInvalidControl: HTMLElement | null = null;

    const findInvalidControl = (group: FormGroup | FormArray) => {
      Object.keys(group.controls).forEach((key) => {
        const control = group.controls[key];
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        }); // Force validation to trigger

        // If the control is invalid and no focus has been set yet
        if (control.invalid && !firstInvalidControl) {
          const element = document.querySelector(`#${key}`) as HTMLElement;

          if (element) {
            firstInvalidControl = element;
          }
        }

        // If the control is a FormGroup or FormArray, recursively check its children
        if (control instanceof FormGroup || control instanceof FormArray) {
          findInvalidControl(control);
        }
      });
    };

    findInvalidControl(formGroup);

    // Focus the first invalid control if found
    if (firstInvalidControl) {
      // 1) scroll it into view
      firstInvalidControl.scrollIntoView({
        behavior: 'smooth', // smooth scroll
        block: 'center', // vertically center it
        inline: 'nearest', // horizontally nearest edge
      });

      firstInvalidControl.focus();
      // Add a 'blink' class to the control
      firstInvalidControl.classList.add('blink');

      // Remove the 'blink' class after the animation completes (e.g., 1 second)
      setTimeout(() => {
        firstInvalidControl.classList.remove('blink');
      }, 1000); // Adjust the duration to match your CSS animation
    }
  }

  validateFormConfig(
    formConfig: FormConfiguration,
    formGroup: FormGroup,
    formMode: FormMode,
  ) {
    formConfig.tabs.forEach((tab) => {
      if (
        !tab.availableInFormModes ||
        tab.availableInFormModes.includes(formMode)
      ) {
        tab.panels?.forEach((panel) => {
          this.validatePanelRecursively(panel, formGroup); // Validate the panel and its nested panels
        });
      }
    });
  }

  setFormControlsEnabled(formGroup: FormGroup, enabled: boolean) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.setFormControlsEnabled(control as any, enabled);
      } else {
        if (enabled) {
          control.enable();
        } else {
          control.disable();
        }
      }
    });
  }

  validatePanelRecursively(panel: FormPanel, formGroup: FormGroup) {
    // Validate the current panel's items
    panel.items?.forEach((item) => {
      const control = formGroup.get([item.propertyName]);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
        //console.log("Checking control", item.propertyName, control);
        // console.log(
        //   'Control errors',
        //   control.errors,
        //   'Control touched',
        //   control.touched,
        //   'Control valid',
        //   control.valid,
        //   'Control value',
        //   control.value
        // );
      } else {
        console.warn(
          `Control with name '${item.propertyName}' not found in form group.`,
        );
      }
    });

    // Recursively validate nested panels (if any)
    panel.panels?.forEach((childPanel) => {
      this.validatePanelRecursively(childPanel, formGroup); // Recursive call for child panels
    });
  }

  updateFormConfigItemByKey(
    formConfig: FormConfiguration,
    propertyKey: string,
    updatedInputs: any,
  ): void {
    formConfig.tabs.forEach((tab) => {
      tab.panels?.forEach((panel) => {
        this.updateItemInPanelRecursively(panel, propertyKey, updatedInputs);
      });
    });
  }

  updateItemInPanelRecursively(
    panel: any, // FormPanel structure
    propertyKey: string,
    updatedInputs: any,
  ): void {
    // Find and update the item in the current panel
    panel.items?.forEach((item) => {
      if (item.propertyName === propertyKey) {
        item.additionalConfig = {
          ...item.additionalConfig,
          ...updatedInputs,
        };
      }
    });

    // Recursively check nested panels
    panel.panels?.forEach((childPanel) => {
      this.updateItemInPanelRecursively(childPanel, propertyKey, updatedInputs);
    });
  }

  /**
   * Extracts the values of a specific property from the filters form data.
   *
   * @param data - The filters form data.
   * @param propertyName - The name of the property to extract.
   * @returns An array of property values.
   */
  extractFiltersFormDataPropertyValues(data, propertyName) {
    const propertyPattern = new RegExp(
      `^filters\\[${propertyName}\\]\\[include]\\[\\d+]$`,
    );
    const propertyValues = [];

    for (const key in data) {
      if (propertyPattern.test(key)) {
        propertyValues.push(data[key]);
      }
    }

    return propertyValues;
  }

  /**
   * Removes properties from an object that match the specified property name.
   * @param data - The object from which to remove properties.
   * @param propertyName - The name of the properties to remove.
   */
  removePropertyByKey(data, propertyName) {
    // Create a regular expression to match the key pattern
    // This will match both simple keys and nested keys with indexes
    const keyPattern = new RegExp(`^${propertyName}(\\[\\d+])?$`);

    // Iterate over the object's keys
    for (const key in data) {
      if (keyPattern.test(key)) {
        delete data[key]; // Remove the key from the object
      }
    }
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  /**
   * Removes form controls from a FormGroup based on a specified pattern.
   * @param formGroup The FormGroup from which to remove the controls.
   * @param pattern The pattern used to match the control names.
   */
  removeControlsByPropertyName(
    formGroup: FormGroup,
    propertyName: string,
    emitEvent = true,
  ): void {
    const controlPattern = `^filters\\[${propertyName}\\]\\[include]\\[\\d+]$`;

    const regex = new RegExp(controlPattern);

    Object.keys(formGroup.controls).forEach((key) => {
      if (regex.test(key)) {
        formGroup.removeControl(key, { emitEvent });
      }
    });
  }

  getCitiesByCountryId(
    formGroup: FormGroup,
    countryPath: string,
  ): Observable<any[]> {
    return formGroup.get([countryPath])!.valueChanges.pipe(
      startWith(formGroup.get([countryPath])!.value), // emit initial value
      distinctUntilChanged(),
      switchMap((countryId) => {
        if (!countryId) {
          // No options until a country is chosen; the "select country first" text
          // is the city picker's placeholder, not an option
          return of([]);
        }

        return this.nomenclatureService.fetchCitiesByCountryId(countryId).pipe(
          map((cities) =>
            this.transformForSelect(
              cities,
              (city) => city.id,
              (city) => city.name,
              null,
            ),
          ),
        );
      }),
    );
  }

  autoYesNoOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.yes'),
      value: true,
    },
    {
      name: this.translateService.instant('forms.form-option.no'),
      value: false,
    },
  ];

  datasetColorsSourceOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.use-dataset-colors',
      ),
      value: 'dataset',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.use-data-source-colors',
      ),
      value: 'data-source',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.use-default-palette',
      ),
      value: 'palette',
    },
  ];

  dataPointSortingOptions: FormOption[] = [
    {
      name: this.translateService.instant(
        'forms.form-option.use-dataset-order',
      ),
      value: 'dataset-order',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.by-value-ascending',
      ),
      value: 'value-asc',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.by-value-descending',
      ),
      value: 'value-desc',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.by-name-ascending',
      ),
      value: 'name-asc',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.by-name-descending',
      ),
      value: 'name-desc',
    },
  ];

  dataLabelsPercentFieldOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.default'),
      value: 'g_percent',
    },
    {
      name: this.translateService.instant('forms.form-option.series-percent'),
      value: 's_percent',
    },
    {
      name: this.translateService.instant('forms.form-option.xaxis-percent'),
      value: 'x_percent',
    },
    {
      name: this.translateService.instant('forms.form-option.yaxis-percent'),
      value: 'y_percent',
    },
    {
      name: this.translateService.instant('forms.form-option.minmax-percent'),
      value: 'm_percent',
    },
  ];

  dataLabelsPlacementOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.inside'),
      value: true,
    },
    {
      name: this.translateService.instant('forms.form-option.outside'),
      value: false,
    },
  ];

  dataLabelsFormatOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.name'),
      value: 'n',
    },
    {
      name: this.translateService.instant('forms.form-option.value'),
      value: 'v',
    },
    {
      name: this.translateService.instant('forms.form-option.percent'),
      value: 'p',
    },
    {
      name: this.translateService.instant('forms.form-option.value-percent'),
      value: 'vp',
    },
    {
      name: this.translateService.instant('forms.form-option.name-value'),
      value: 'nv',
    },
    {
      name: this.translateService.instant('forms.form-option.name-perecent'),
      value: 'np',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.name-value-percent',
      ),
      value: 'nvp',
    },
  ];

  stackLabelsFormatOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.value'),
      value: 'v',
    },
    {
      name: this.translateService.instant('forms.form-option.percent'),
      value: 'p',
    },
    {
      name: this.translateService.instant('forms.form-option.value-percent'),
      value: 'vp',
    },
  ];

  settingsColorSchemeOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.from-dataset'),
      value: 'dataset',
    },
    {
      name: this.translateService.instant('forms.form-option.from-data-source'),
      value: 'datasource',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.from-custom-palette',
      ),
      value: 'custom',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.from-default-palette',
      ),
      value: 'default',
    },
  ];

  exportFormats = [
    {
      value: NomOutputFormat.DOCX,
      name: this.translateService.instant('forms.form-option.msword'),
    },
    {
      value: NomOutputFormat.CSV,
      name: this.translateService.instant('forms.form-option.msexcel'),
    },
    {
      value: NomOutputFormat.HTML,
      name: this.translateService.instant('forms.form-option.html'),
    },
  ];

  itemHorizontalAlignOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.left'),
      value: 'left',
    },
    {
      name: this.translateService.instant('forms.form-option.center'),
      value: 'center',
    },
    {
      name: this.translateService.instant('forms.form-option.right'),
      value: 'right',
    },
  ];

  itemVerticalAlignOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.top'),
      value: 'top',
    },
    {
      name: this.translateService.instant('forms.form-option.center'),
      value: 'middle',
    },
    {
      name: this.translateService.instant('forms.form-option.bottom'),
      value: 'bottom',
    },
  ];

  legendLayoutOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.auto'),
      value: 'auto',
    },
    {
      name: this.translateService.instant('forms.form-option.horizontal'),
      value: 'horizontal',
    },
    {
      name: this.translateService.instant('forms.form-option.vertical'),
      value: 'vertical',
    },
    {
      name: this.translateService.instant('forms.form-option.proximate'),
      value: 'proximate',
    },
  ];

  fillOpacityOptions: FormOption[] = [
    { name: '0%', value: 0 },
    { name: '10%', value: 0.1 },
    { name: '20%', value: 0.2 },
    { name: '30%', value: 0.3 },
    { name: '40%', value: 0.4 },
    { name: '50%', value: 0.5 },
    { name: '60%', value: 0.6 },
    { name: '70%', value: 0.7 },
    { name: '80%', value: 0.8 },
    { name: '90%', value: 0.9 },
    { name: '100%', value: 1 },
  ];

  fontSizeOptions: FormOption[] = [
    { name: '0.75rem', value: 10 },
    { name: '11px', value: 11 },
    { name: '12px', value: 12 },
    { name: '13px', value: 13 },
    { name: '14px', value: 14 },
    { name: '15px', value: 15 },
    { name: '16px', value: 16 },
    { name: '17px', value: 17 },
    { name: '18px', value: 18 },
    { name: '19px', value: 19 },
    { name: '20px', value: 20 },
    { name: '21px', value: 21 },
    { name: '22px', value: 22 },
    { name: '23px', value: 23 },
    { name: '24px', value: 24 },
    { name: '25px', value: 25 },
    { name: '26px', value: 26 },
    { name: '27px', value: 27 },
    { name: '28px', value: 28 },
  ];

  summaryFilters: SummaryFilter[] = [
    {
      value: 'post_language_id',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-languages',
      ),
    },
    {
      value: 'post_source_id',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-sources',
      ),
    },
    {
      value: 'post_source_brand_name',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-brand-names',
      ),
    },
    {
      value: 'post_author_name',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-authors',
      ),
    },
    {
      value: 'post_social_author_id',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-social-users',
      ),
    },
    {
      value: 'section_country_id',
      name: this.translateService.instant(
        'forms.form-option.number-distinct-countries',
      ), // Corrected translation key
    },
    {
      value: 'source_type_id',
      name: this.translateService.instant(
        'forms.form-option.volume-breakdown-by-source-type',
      ),
    },
    {
      value: 'post_sentiment_id',
      name: this.translateService.instant(
        'forms.form-option.volume-breakdown-by-sentiment',
      ),
    },
    {
      value: 'source_media_channel_id',
      name: this.translateService.instant(
        'forms.form-option.volume-breakdown-by-media-channel',
      ),
    },
    {
      value: 'source_media_channel_type_id',
      name: this.translateService.instant(
        'forms.form-option.volume-breakdown-by-media-channel-type',
      ),
    },
    {
      value: 'post_id',
      name: this.translateService.instant('forms.form-option.total-volume'),
    },
    {
      value: 'post_potential_reach',
      name: this.translateService.instant(
        'forms.form-option.total-potential-reach',
      ),
    },
    {
      value: 'post_estimated_reach',
      name: this.translateService.instant(
        'forms.form-option.total-estimated-reach',
      ),
    },
    {
      value: 'post_ave',
      name: this.translateService.instant('forms.form-option.total-ave'),
    },
    // Adding missing fields from the AngularJS app
    {
      value: 'post_social_followers',
      name: this.translateService.instant(
        'forms.form-option.total-source-followers',
      ),
    },
    {
      value: 'post_source_visits_per_day',
      name: this.translateService.instant(
        'forms.form-option.total-source-daily-visits',
      ),
    },
    {
      value: 'post_social_likes_count',
      name: this.translateService.instant('forms.form-option.total-like-count'),
    },
    {
      value: 'post_social_dislikes_count',
      name: this.translateService.instant(
        'forms.form-option.total-dislikes-count',
      ),
    },
    {
      value: 'post_social_comments_count',
      name: this.translateService.instant(
        'forms.form-option.total-comment-count',
      ),
    },
    {
      value: 'post_social_shares_count',
      name: this.translateService.instant(
        'forms.form-option.total-share-count',
      ),
    },
    {
      value: 'post_social_bookmarks_count',
      name: this.translateService.instant(
        'forms.form-option.total-bookmark-count',
      ),
    },
    {
      value: 'post_social_quotes_count',
      name: this.translateService.instant(
        'forms.form-option.total-quote-count',
      ),
    },
    {
      value: 'post_stats_views',
      name: this.translateService.instant('forms.form-option.total-post-views'),
    },
    {
      value: 'post_social_reactions_count',
      name: this.translateService.instant(
        'forms.form-option.total-reactions-count',
      ),
    },
    {
      value: 'post_social_interactions_count',
      name: this.translateService.instant(
        'forms.form-option.total-interactions-count',
      ),
    },
    {
      value: 'post_social_engagements_count',
      name: this.translateService.instant(
        'forms.form-option.total-engagement-count',
      ),
    },
    {
      value: 'post_attachment_video_length_seconds',
      name: this.translateService.instant(
        'forms.form-option.total-video-length',
      ),
    },
  ];

  topInfluencerFilters: SummaryFilter[] = [
    {
      value: 'post_id_count',
      name: this.translateService.instant('forms.form-option.posts'),
    },
    {
      value: 'post_social_followers_sum',
      name: this.translateService.instant('forms.form-option.potential-reach'),
    },
    {
      value: 'post_social_followers_avg',
      name: this.translateService.instant(
        'forms.form-option.potential-reach-per-post',
      ),
    },
    {
      value: 'post_social_reactions_sum',
      name: this.translateService.instant('forms.form-option.reactions'),
    },
    {
      value: 'post_social_interactions_sum',
      name: this.translateService.instant('forms.form-option.interactions'),
    },
    {
      value: 'post_social_engagements_sum',
      name: this.translateService.instant('forms.form-option.engagement'),
    },
    {
      value: 'post_social_engagements_avg',
      name: this.translateService.instant(
        'forms.form-option.engagement-per-post',
      ),
    },
    {
      value: 'post_social_interaction_ratio',
      name: this.translateService.instant(
        'forms.form-option.interaction-ratio',
      ),
    },
    {
      value: 'post_social_engagement_ratio',
      name: this.translateService.instant('forms.form-option.engagement-ratio'),
    },
    {
      value: 'post_social_author_statuses_count_avg',
      name: this.translateService.instant(
        'forms.form-option.total-statuses-count',
      ),
    },
  ];

  contentSortProperties: SummaryFilter[] = [
    {
      value: 'post_time_published',
      name: this.translateService.instant('forms.form-option.time-published'),
    },
    {
      value: 'post_reach_v2',
      name: this.translateService.instant('forms.form-option.potential-reach'),
    },
    {
      value: 'post_source_visits_per_day',
      name: this.translateService.instant(
        'forms.form-option.source-daily-visits',
      ),
    },
    {
      value: 'source_rank_global',
      name: this.translateService.instant(
        'forms.form-option.source-global-rank',
      ),
    },
    {
      value: 'source_rank_country',
      name: this.translateService.instant(
        'forms.form-option.source-domestic-rank',
      ),
    },
    {
      value: 'post_social_followers',
      name: this.translateService.instant('forms.form-option.followers'),
    },
    {
      value: 'post_social_reactions_count',
      name: this.translateService.instant('forms.form-option.reactions'),
    },
    {
      value: 'post_social_interactions_count',
      name: this.translateService.instant('forms.form-option.interactions'),
    },
    {
      value: 'post_social_engagements_count',
      name: this.translateService.instant('forms.form-option.engagements'),
    },
    {
      value: 'post_social_likes_count',
      name: this.translateService.instant('forms.form-option.likes'),
    },
    {
      value: 'post_social_dislikes_count',
      name: this.translateService.instant('forms.form-option.dislikes'),
    },
    {
      value: 'post_social_shares_count',
      name: this.translateService.instant('forms.form-option.shares'),
    },
  ];

  exportOutputLanguages: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.english'),
      value: NomLanguages.ENGLISH,
    },
    {
      name: this.translateService.instant('forms.form-option.arabic'),
      value: NomLanguages.ARABIC,
    },
    {
      name: this.translateService.instant('forms.form-option.bulgarian'),
      value: NomLanguages.BULGARIAN,
    },
  ];

  quickStoryReportLanguages: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.english'),
      value: NomLanguages.ENGLISH,
    },
    {
      name: this.translateService.instant('forms.form-option.arabic'),
      value: NomLanguages.ARABIC,
    },
    {
      name: this.translateService.instant('forms.form-option.bulgarian'),
      value: NomLanguages.BULGARIAN,
    },
  ];

  exportOutputFormats: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.msword'),
      value: NomOutputFormat.DOCX,
    },
    {
      name: this.translateService.instant('forms.form-option.msexcel'),
      value: NomOutputFormat.CSV,
    },
  ];

  externalShareOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.content-excerpt'),
      value: NomExternalShareOption.CONTENT_EXCERPT,
    },
    {
      name: this.translateService.instant('forms.form-option.full-content'),
      value: NomExternalShareOption.FULL_CONTENT,
    },
    {
      name: this.translateService.instant('forms.form-option.ai-summary'),
      value: NomExternalShareOption.AI_SUMMARY,
    },
  ];

  scheduleOutputFormats: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.msword'),
      value: NomOutputFormat.DOCX,
    },
    {
      name: this.translateService.instant('forms.form-option.msexcel'),
      value: NomOutputFormat.CSV,
    },
    {
      name: this.translateService.instant('forms.form-option.html'),
      value: NomOutputFormat.HTML,
    },

    {
      name: this.translateService.instant('forms.form-option.mattermost'),
      value: NomOutputFormat.MATTERMOST,
    },
    {
      name: this.translateService.instant('forms.form-option.pdf'),
      value: NomOutputFormat.PDF,
    },
  ];

  alertTriggerTypes: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.on-new-feed-post'),
      value: NomAlertTriggerType.NEW_POSTS,
    },
    {
      name: this.translateService.instant('forms.form-option.on-volume-change'),
      value: NomAlertTriggerType.PERCENT_CHANGE,
    },
  ];

  formatOutputTypes: FormOption[] = [
    {
      name: this.translateService.instant(
        'forms.form-option-output-type.attachment',
      ),
      value: 1,
    },
    {
      name: this.translateService.instant('forms.form-option-output-type.link'),
      value: 2,
    },
    {
      name: this.translateService.instant(
        'forms.form-option-output-type.attachment-and-link',
      ),
      value: 3,
    },
  ];

  alertDeviationOptions: FormOption[] = [
    { name: '10%', value: 10 },
    { name: '20%', value: 20 },
    { name: '30%', value: 30 },
    { name: '40%', value: 40 },
    { name: '50%', value: 50 },
    { name: '60%', value: 60 },
    { name: '70%', value: 70 },
    { name: '80%', value: 80 },
    { name: '90%', value: 90 },
    { name: '100%', value: 100 },
    { name: '200%', value: 200 },
    { name: '300%', value: 300 },
    { name: '400%', value: 400 },
    { name: '500%', value: 500 },
    { name: '600%', value: 600 },
    { name: '700%', value: 700 },
    { name: '800%', value: 800 },
    { name: '900%', value: 900 },
    { name: '1000%', value: 1000 },
  ];

  alertDeviationDirection: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.up'),
      value: NomAlertDeviationDirection.Up,
    },
    {
      name: this.translateService.instant('forms.form-option.down'),
      value: NomAlertDeviationDirection.Down,
    },
    {
      name: this.translateService.instant('forms.form-option.up-or-down'),
      value: NomAlertDeviationDirection.UpOrDown,
    },
  ];

  alertDeviationPeriod: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.last-hour'),
      value: 3600,
    },
    {
      name: this.translateService.instant('forms.form-option.last-2-hours'),
      value: 7200,
    },
    {
      name: this.translateService.instant('forms.form-option.last-3-hours'),
      value: 10800,
    },
    {
      name: this.translateService.instant('forms.form-option.last-6-hours'),
      value: 21600,
    },
    {
      name: this.translateService.instant('forms.form-option.last-12-hours'),
      value: 43200,
    },
    {
      name: this.translateService.instant('forms.form-option.last-24-hours'),
      value: 86400,
    },
    {
      name: this.translateService.instant('forms.form-option.last-2-days'),
      value: 172800,
    },
    {
      name: this.translateService.instant('forms.form-option.last-3-days'),
      value: 259200,
    },
    {
      name: this.translateService.instant('forms.form-option.last-4-days'),
      value: 345600,
    },
    {
      name: this.translateService.instant('forms.form-option.last-5-days'),
      value: 432000,
    },
    {
      name: this.translateService.instant('forms.form-option.last-6-days'),
      value: 518400,
    },
    {
      name: this.translateService.instant('forms.form-option.last-week'),
      value: 604800,
    },
    {
      name: this.translateService.instant('forms.form-option.last-2-weeks'),
      value: 1209600,
    },
  ];

  weekDays: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.monday'),
      value: 1,
    },
    {
      name: this.translateService.instant('forms.form-option.tuesday'),
      value: 2,
    },
    {
      name: this.translateService.instant('forms.form-option.wednesday'),
      value: 3,
    },
    {
      name: this.translateService.instant('forms.form-option.thursday'),
      value: 4,
    },
    {
      name: this.translateService.instant('forms.form-option.friday'),
      value: 5,
    },
    {
      name: this.translateService.instant('forms.form-option.saturday'),
      value: 6,
    },
    {
      name: this.translateService.instant('forms.form-option.sunday'),
      value: 7,
    },
  ];

  mattermostChannels: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.public-channel'),
      value: 'public',
    },
    {
      name: this.translateService.instant('forms.form-option.private-channel'),
      value: 'private',
    },
  ];

  ruleLimitPeriods: FormOption[] = [
    { name: this.translateService.instant('forms.form-option.day'), value: 4 },
    {
      name: this.translateService.instant('forms.form-option.month'),
      value: 6,
    },
  ];

  pieViewTypeOptions: FormOption[] = [
    {
      name: this.translateService.instant('forms.form-option.pie-full-circle'),
      value: 'circle',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.pie-semi-circle-donut',
      ),
      value: 'semi_circle_donut',
    },
  ];

  duplicateFeedOptions: FormOption[] = [
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-widgets',
      ),
      value: 'duplicateWidgets',
    },
    {
      name: this.translateService.instant('forms.form-option.duplicate-alerts'),
      value: 'duplicateAlerts',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-export-schedules',
      ),
      value: 'duplicateExportSchedules',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-output-settings',
      ),
      value: 'duplicateOutputSettings',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-content-filters',
      ),
      value: 'duplicateContentFilters',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-removed-items',
      ),
      value: 'duplicateRemovedItems',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.preserve-linked-dimensions',
      ),
      value: 'preserveLinkedDimensions',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-permissions',
      ),
      value: 'duplicatePermissions',
    },
  ];

  duplicateDashboardOptions: FormOption[] = [
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-export-schedules',
      ),
      value: 'duplicateExportSchedules',
    },
    {
      name: this.translateService.instant(
        'forms.form-option.duplicate-permissions',
      ),
      value: 'duplicatePermissions',
    },
  ];
}

export function requiredNonZero(): ValidatorFn {
  return (control: AbstractControl) => {
    return control.value === 0 ||
      control.value === null ||
      control.value === undefined ||
      control.value === ''
      ? { requiredNonZero: true }
      : null;
  };
}
